import {
  DatePicker,
  type DatePickerProps,
  getDate,
  momentToCalendarDate,
} from '@breezeai-frontend/cargo-ui';
import { type DateValue } from '@internationalized/date';
import { type FormApi } from '@tanstack/react-form';
import { type zodValidator } from '@tanstack/zod-form-adapter';
import moment from 'moment';

import { type DeliveryFieldsFormData } from './types';

interface EtaFieldProps extends DatePickerProps<DateValue> {
  form: FormApi<DeliveryFieldsFormData, typeof zodValidator>;
}

/**
 * This component is currently only used for WTW in the following forms: CertificateFlowForm and PolicyEditForm.
 * When we'll complete our form refactoring work and we will re-use this component for Breeze, we need to be mindful
 * that the dates are not limited by the open cover in this component, which is something that Breeze cares about.
 */
export function EtaField({ form, ...props }: EtaFieldProps) {
  return (
    <form.Field
      name="eta"
      validators={{
        onChangeListenTo: ['etd'],
        onChange: ({ value, fieldApi }) => {
          const etd = fieldApi.form.getFieldValue('etd');
          if (etd && moment(etd).isAfter(moment(value))) {
            return 'Date of arrival must be after date of departure';
          }
          return undefined;
        },
      }}
      children={(field) => {
        const etd = field.form.state.values.etd;
        const etdMoment = etd ? moment(etd) : undefined;
        const minDate = etdMoment ?? undefined;

        return (
          <DatePicker
            data-testid="eta-date-picker"
            label="Date of Arrival"
            defaultValue={getDate(field.state.value)}
            minValue={minDate ? momentToCalendarDate(minDate) : undefined}
            validate={() => {
              if (field.state.meta.errors.length > 0) {
                return field.state.meta.errors.join(', ');
              }
              return true;
            }}
            id={field.name}
            name={field.name}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.toString())}
            isDateUnavailable={(date) => {
              // Dates always available if there is no min date.
              if (!minDate) {
                return false;
              }

              const etaMoment = moment(date.toString());
              return etaMoment.isBefore(minDate);
            }}
            {...props}
          />
        );
      }}
    />
  );
}
